import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { Table, Typography, Tag, Button, Space, Tooltip, Modal, Input, Select, Spin, Empty, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LinkOutlined, FileTextOutlined, EyeOutlined, SearchOutlined, CloseOutlined, MailOutlined, PaperClipOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';

import PDFViewer from 'modules/document-processing/components/PDFViewer';
import { useGuestAuth } from '../../GuestAuthContext';
import { fetchGuestDocuments, fetchAssessmentReportReplies } from '../../api';
import type { GuestDocument, AssessmentReportStatus, AssessmentReportReply } from '../../api';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const STATUS_COLORS: Record<GuestDocument['status'], string> = {
    uploaded: 'blue',
    processing: 'orange',
    analysed: 'cyan',
    completed: 'green',
};

const ASSESSMENT_STATUS_COLORS: Record<Exclude<AssessmentReportStatus, null>, string> = {
    submitted: 'default',
    reply_received: 'gold',
    completed: 'green',
};

const WaitingForReplyState: FC<{ message: string }> = ({ message }) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100%',
            minHeight: 260,
            padding: 24,
        }}
    >
        <style>{`
            @keyframes waitingForReplyPulse {
                0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.2); }
                50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(24, 144, 255, 0); }
            }
        `}</style>
        <div
            style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f0f5ff, #d6e4ff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 20,
                animation: 'waitingForReplyPulse 2s ease-in-out infinite',
            }}
        >
            <ClockCircleOutlined style={{ fontSize: 28, color: '#1890ff' }} />
        </div>
        <div style={{ fontSize: 15, lineHeight: 1.6, color: '#4a5568', maxWidth: 320 }}>
            {message}
        </div>
    </div>
);

function filenameFromUrl(url: string): string {
    try {
        const parts = new URL(url).pathname.split('/');
        return decodeURIComponent(parts[parts.length - 1]) || url;
    } catch {
        return url;
    }
}

const NotAvailable: FC = () => (
    <span style={{ color: '#a0aec0', fontSize: 12 }}>
        <FormattedMessage id="guest.documents.analysisNotAvailable" />
    </span>
);

const GuestDocumentsPage: FC = () => {
    const { formatMessage } = useIntl();
    const { token } = useGuestAuth();
    const [rows, setRows] = useState<GuestDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Draft state — what the user is typing / selecting
    const [nameDraft, setNameDraft] = useState('');
    const [statusDraft, setStatusDraft] = useState<string | undefined>(undefined);
    const [caseNumberDraft, setCaseNumberDraft] = useState('');

    // Applied state — what the table actually filters by
    const [nameSearch, setNameSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [caseNumberSearch, setCaseNumberSearch] = useState('');

    // Which document-type tab (mirrors the tabs on the main Documents page) is active
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

    // PDF preview modal
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewFilename, setPreviewFilename] = useState('');

    // Assessment report replies modal
    const [repliesDocument, setRepliesDocument] = useState<GuestDocument | null>(null);
    const [replies, setReplies] = useState<AssessmentReportReply[]>([]);
    const [repliesLoading, setRepliesLoading] = useState(false);
    const [repliesError, setRepliesError] = useState(false);

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        setError(false);
        fetchGuestDocuments(token)
            .then(setRows)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [token]);

    const handleApply = useCallback(() => {
        setNameSearch(nameDraft);
        setStatusFilter(statusDraft);
        setCaseNumberSearch(caseNumberDraft);
    }, [nameDraft, statusDraft, caseNumberDraft]);

    const handleClear = useCallback(() => {
        setNameDraft('');
        setStatusDraft(undefined);
        setCaseNumberDraft('');
        setNameSearch('');
        setStatusFilter(undefined);
        setCaseNumberSearch('');
    }, []);

    const handleFilterKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); handleApply(); }
        if (e.key === 'Escape') { e.preventDefault(); handleClear(); }
    };

    const statusOptions = useMemo(
        () =>
            (['uploaded', 'processing', 'analysed', 'completed'] as GuestDocument['status'][]).map(s => ({
                label: formatMessage({ id: `guest.documents.status.${s}` }),
                value: s,
            })),
        [formatMessage],
    );

    const filteredRows = useMemo(() => {
        const lowerSearch = nameSearch.toLowerCase();
        const lowerCaseNumber = caseNumberSearch.toLowerCase();
        return rows.filter(row => {
            const matchesName = !lowerSearch || filenameFromUrl(row.originalDocumentUrl).toLowerCase().includes(lowerSearch);
            const matchesStatus = !statusFilter || row.status === statusFilter;
            const matchesCaseNumber = !lowerCaseNumber || (row.caseNumber || '').toLowerCase().includes(lowerCaseNumber);
            return matchesName && matchesStatus && matchesCaseNumber;
        });
    }, [rows, nameSearch, statusFilter, caseNumberSearch]);

    // Group into tabs by document type, mirroring the Invoices/Contracts/Quotations/... tabs on the main Documents page.
    const tabsData = useMemo(() => {
        const byType = new Map<string, GuestDocument[]>();
        filteredRows.forEach(row => {
            const docs = byType.get(row.queueType) ?? [];
            docs.push(row);
            byType.set(row.queueType, docs);
        });
        return Array.from(byType.entries()).map(([type, docs]) => ({
            key: type,
            label: formatMessage({ id: `guest.documents.queueType.${type}`, defaultMessage: type }),
            docs,
        }));
    }, [filteredRows, formatMessage]);

    const activeKey = activeTab && tabsData.some(t => t.key === activeTab) ? activeTab : tabsData[0]?.key;

    const openPreview = (url: string) => {
        setPreviewFilename(filenameFromUrl(url));
        setPreviewUrl(url);
    };

    const openReplies = useCallback((record: GuestDocument) => {
        setRepliesDocument(record);
        if (!token) return;

        setRepliesLoading(true);
        setRepliesError(false);
        fetchAssessmentReportReplies(token, record.id)
            .then(data => setReplies(data.replies))
            .catch(() => setRepliesError(true))
            .finally(() => setRepliesLoading(false));
    }, [token]);

    const closeReplies = () => {
        setRepliesDocument(null);
        setReplies([]);
        setRepliesError(false);
    };

    // Assessment reports have no AI-analysis result — their "result" is the
    // service provider's reply instead, so opening the document should take
    // the customer straight there rather than to a PDF preview.
    const openDocument = useCallback((record: GuestDocument) => {
        if (record.assessmentReportStatus) {
            openReplies(record);
        } else if (record.analysisDocumentUrl) {
            openPreview(record.analysisDocumentUrl);
        } else {
            openPreview(record.originalDocumentUrl);
        }
    }, [openReplies]);

    const columns: ColumnsType<GuestDocument> = [
        {
            title: formatMessage({ id: 'guest.documents.column.name' }),
            dataIndex: 'originalDocumentUrl',
            key: 'name',
            ellipsis: true,
            render: (url: string, record: GuestDocument) => (
                <Space>
                    <FileTextOutlined style={{ color: '#a0aec0' }} />
                    <Tooltip title={filenameFromUrl(url)}>
                        <Button type="link" style={{ padding: 0, fontWeight: 500 }} onClick={() => openDocument(record)}>
                            {filenameFromUrl(url)}
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: formatMessage({ id: 'guest.documents.column.status' }),
            key: 'status',
            width: 150,
            render: (_: unknown, record: GuestDocument) =>
                record.assessmentReportStatus ? (
                    <Tag color={ASSESSMENT_STATUS_COLORS[record.assessmentReportStatus]}>
                        {formatMessage({ id: `guest.documents.caseStatus.${record.assessmentReportStatus}` })}
                    </Tag>
                ) : (
                    <Tag color={STATUS_COLORS[record.status]}>
                        {formatMessage({ id: `guest.documents.status.${record.status}` })}
                    </Tag>
                ),
        },
        {
            title: formatMessage({ id: 'landing.documents.column.caseNumber', defaultMessage: 'Vorgangsnummer' }),
            dataIndex: 'caseNumber',
            key: 'caseNumber',
            width: 140,
            render: (caseNumber: string | null) => caseNumber || '-',
        },
        {
            title: formatMessage({ id: 'guest.documents.column.createdAt' }),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: (date: string) => (
                <span style={{ color: '#718096' }}>
                    {new Date(date).toLocaleString(undefined, {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                    })}
                </span>
            ),
        },
        {
            title: formatMessage({ id: 'guest.documents.column.actions' }),
            key: 'actions',
            width: 260,
            render: (_: unknown, record: GuestDocument) => (
                <Space wrap>
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openPreview(record.originalDocumentUrl)}
                    >
                        <FormattedMessage id="guest.documents.viewOriginal" />
                    </Button>
                    {!record.assessmentReportStatus && (
                        record.analysisDocumentUrl ? (
                            <Button
                                size="small"
                                icon={<LinkOutlined />}
                                onClick={() => openPreview(record.analysisDocumentUrl!)}
                            >
                                <FormattedMessage id="guest.documents.viewAnalysis" />
                            </Button>
                        ) : (
                            <NotAvailable />
                        )
                    )}
                    {record.assessmentReportStatus && (
                        <Button
                            size="small"
                            icon={<MailOutlined />}
                            onClick={() => openReplies(record)}
                        >
                            <FormattedMessage id="guest.documents.viewReplies" />
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ minHeight: '100%', background: '#f4f6f9', padding: '40px 24px' }}>
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                <div style={{ marginBottom: 28 }}>
                    <Title level={2} style={{ margin: 0, color: '#1a1a2e', fontWeight: 700 }}>
                        <FormattedMessage id="guest.documents.title" />
                    </Title>
                    <Paragraph style={{ margin: '6px 0 0', color: '#718096', fontSize: 15 }}>
                        <FormattedMessage id="guest.documents.subtitle" defaultMessage="View and track the documents you've uploaded." />
                    </Paragraph>
                </div>

                {/* Filter toolbar — Enter applies, Escape clears */}
                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: 20,
                        marginBottom: 20,
                    }}
                    onKeyDown={handleFilterKeyDown}
                >
                    <Space style={{ flexWrap: 'wrap' }} size={12}>
                        <Input
                            placeholder={formatMessage({ id: 'guest.documents.filter.searchName' })}
                            value={nameDraft}
                            onChange={e => setNameDraft(e.target.value)}
                            onPressEnter={handleApply}
                            style={{ width: 280 }}
                        />
                        <Select
                            placeholder={formatMessage({ id: 'guest.documents.column.status' })}
                            allowClear
                            options={statusOptions}
                            value={statusDraft}
                            onChange={setStatusDraft}
                            style={{ width: 190 }}
                        />
                        <Input
                            placeholder={formatMessage({ id: 'landing.documents.column.caseNumber', defaultMessage: 'Vorgangsnummer' })}
                            value={caseNumberDraft}
                            onChange={e => setCaseNumberDraft(e.target.value)}
                            onPressEnter={handleApply}
                            style={{ width: 190 }}
                        />
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleApply}
                        >
                            <FormattedMessage id="guest.documents.filter.apply" />
                        </Button>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={handleClear}
                            disabled={!nameDraft && !statusDraft && !caseNumberDraft && !nameSearch && !statusFilter && !caseNumberSearch}
                        >
                            <FormattedMessage id="guest.documents.filter.reset" />
                        </Button>
                    </Space>
                </div>

                <div
                    style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}
                >
                    {tabsData.length > 0 ? (
                        <Tabs
                            activeKey={activeKey}
                            onChange={setActiveTab}
                            style={{ padding: '4px 20px 0' }}
                        >
                            {tabsData.map(tab => (
                                <TabPane tab={`${tab.label} (${tab.docs.length})`} key={tab.key}>
                                    <Table<GuestDocument>
                                        dataSource={tab.docs}
                                        columns={columns}
                                        rowKey="id"
                                        loading={loading}
                                        pagination={{ pageSize: 10, showSizeChanger: false }}
                                    />
                                </TabPane>
                            ))}
                        </Tabs>
                    ) : (
                        <Table<GuestDocument>
                            dataSource={filteredRows}
                            columns={columns}
                            rowKey="id"
                            loading={loading}
                            pagination={{ pageSize: 10, showSizeChanger: false }}
                            locale={{
                                emptyText: error
                                    ? formatMessage({ id: 'guest.documents.loadError' })
                                    : formatMessage({ id: 'guest.documents.empty' }),
                            }}
                        />
                    )}
                </div>
            </div>

            {/* PDF preview */}
            <Modal
                open={!!previewUrl}
                title={previewFilename}
                onCancel={() => setPreviewUrl(null)}
                footer={null}
                width="80vw"
                style={{ top: 20 }}
                bodyStyle={{ padding: 0, height: '80vh' }}
                destroyOnClose
            >
                {previewUrl && (
                    <PDFViewer pdfUrl={previewUrl} filename={previewFilename} hideControls />
                )}
            </Modal>

            {/* Assessment report replies */}
            <Modal
                open={!!repliesDocument}
                title={repliesDocument
                    ? filenameFromUrl(repliesDocument.originalDocumentUrl)
                    : formatMessage({ id: 'guest.documents.replies.title' })}
                onCancel={closeReplies}
                footer={null}
                width="95vw"
                style={{ top: 10 }}
                bodyStyle={{ padding: 0, height: '90vh' }}
                destroyOnClose
            >
                {repliesDocument && (
                    <div style={{ display: 'flex', height: '100%' }}>
                        <div style={{ width: '65%', flexShrink: 0, borderRight: '1px solid #f0f0f0', padding: 16 }}>
                            <PDFViewer
                                pdfUrl={repliesDocument.originalDocumentUrl}
                                filename={filenameFromUrl(repliesDocument.originalDocumentUrl)}
                                hideControls
                            />
                        </div>
                        <div style={{ width: '35%', flexShrink: 0, minWidth: 0, padding: 16, overflowY: 'auto' }}>
                            {repliesLoading ? (
                                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                                    <Spin size="large" />
                                </div>
                            ) : repliesError ? (
                                <Empty description={formatMessage({ id: 'guest.documents.replies.loadError' })} />
                            ) : replies.length === 0 ? (
                                <WaitingForReplyState message={formatMessage({ id: 'guest.documents.replies.empty' })} />
                            ) : (
                                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                    {replies.map(reply => (
                                        <div
                                            key={reply.id}
                                            style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                                <div>
                                                    <Text strong>{reply.subject}</Text>
                                                    <br />
                                                    <Text type="secondary">{reply.fromAddress}</Text>
                                                </div>
                                                <Text type="secondary">
                                                    {new Date(reply.receivedAt).toLocaleString(undefined, {
                                                        year: 'numeric', month: '2-digit', day: '2-digit',
                                                        hour: '2-digit', minute: '2-digit',
                                                    })}
                                                </Text>
                                            </div>

                                            {reply.bodyHtml ? (
                                                <iframe
                                                    title={reply.subject}
                                                    srcDoc={reply.bodyHtml}
                                                    sandbox=""
                                                    style={{ width: '100%', height: 260, border: '1px solid #f0f0f0', borderRadius: 4, background: '#fff' }}
                                                />
                                            ) : (
                                                <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                                                    {reply.bodyText}
                                                </Paragraph>
                                            )}

                                            {reply.attachments.length > 0 && (
                                                <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
                                                    {reply.attachments.map(attachment => (
                                                        <a
                                                            key={attachment.id}
                                                            href={attachment.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <PaperClipOutlined /> {attachment.fileName}
                                                        </a>
                                                    ))}
                                                </Space>
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default GuestDocumentsPage;
